import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Container from "react-bootstrap/Container";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ControlledTextField from "../../../components/ControlledTextField";
import { Button, Col, Form, Row } from "react-bootstrap";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from "firebase/auth";
import "./index.css";
import { CardText, CardTitle } from "reactstrap";
import { toast } from "react-toastify";
import { auth } from "../../../firebase";
import "firebase/auth";
import GoogleIcon from '../../../assets/icons/google-icon.png'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import authBackground from "../../../assets/images/authBackground.PNG";
import logo from "../../../assets/images/logoWOBGD.PNG";
import Swal from "sweetalert2";

type FormData = {
  email: string;
  password: string;
};

const SignIn = () => {
  const navigate = useNavigate();
  const [isLogging, setIsLogging] = useState(false);

  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [dots, setDots] = useState("");

  const { control, handleSubmit, formState: { errors, isValid }, getValues, reset } = useForm<FormData>({
    mode: 'onChange', // This enables real-time validation
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleForgotPasswordMode = () => {
    setIsForgotPasswordMode(true);
    reset({ email: getValues("email"), password: "" });
  };

  const handleCancelForgotPassword = () => {
    setIsForgotPasswordMode(false);
    reset({ email: "", password: "" }); // Clear form
  };

  const handleForgotPassword = async (data: FormData) => {
    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, data.email, {
        url: `${window.location.origin}/sign-in`,
        handleCodeInApp: false,
      });

      setIsResettingPassword(false);

      Swal.fire({
        title: "Password Reset Email Sent!",
        html: `
          <p>We've sent a password reset link to <strong>${data.email}</strong></p>
          <p>Please check your email (including spam/junk folder) and follow the instructions to reset your password.</p>
          <small class="text-muted">If you don't receive the email within a few minutes, please try again.</small>
        `,
        icon: "success",
        confirmButtonColor: "#590b21",
        confirmButtonText: "OK"
      }).then(() => {
        setIsForgotPasswordMode(false);
        reset({ email: "", password: "" });
      });

    } catch (error: any) {
      setIsResettingPassword(false);
      console.error("Password reset error:", error);

      let errorMessage = "Failed to send password reset email. Please try again.";

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "No account found with this email address.";
          break;
        case 'auth/invalid-email':
          errorMessage = "Please enter a valid email address.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Too many requests. Please try again later.";
          break;
        default:
          errorMessage = error.message || "An error occurred while sending the password reset email.";
      }

      toast.error(errorMessage);
    }
  };

  const handleUserRedirect = async (userCredential: any) => {
    const accessToken = await userCredential.user.getIdToken();
    localStorage.setItem("accessToken", accessToken);

    // Get user roles from token claims
    const idTokenResult = await userCredential.user.getIdTokenResult();
    const roles = idTokenResult.claims.roles || [];

    // Store user info
    if (userCredential.user.email) {
      localStorage.setItem("email", userCredential.user.email);
    }
    if (userCredential.user.photoURL) {
      localStorage.setItem("photoURL", userCredential.user.photoURL);
    }
    if (userCredential.user.uid) {
      localStorage.setItem("ownerID", userCredential.user.uid);
    }

    // Only allow super_admin to proceed
    if (roles.includes('super_admin')) {
      navigate('/');
      toast.success("Welcome Super Admin!");
    } else {
      // Sign out the user immediately if not super_admin
      await auth.signOut();
      localStorage.clear();
      toast.error("You don't have permission to access this portal. Please contact support.");
    }
  };

  const handleSignIn = async (data: FormData) => {
    setIsLogging(true);
    try {
      const { email, password } = data;
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      await handleUserRedirect(userCredential);
    } catch (error: any) {
      console.error("Sign-in error:", error);
      if (error.code === "auth/wrong-password" || error.code === "auth/user-not-found" || error.code === "auth/invalid-credential") {
        toast.error("Invalid email or password");
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Account temporarily locked due to too many attempts");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsLogging(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      const userCredential = await signInWithPopup(auth, provider);
      await handleUserRedirect(userCredential);
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      if (error.code === "auth/account-exists-with-different-credential") {
        toast.error("An account already exists with this email");
      } else {
        toast.error("Failed to sign in with Google");
      }
    }
  };

  const onSubmit = (data: FormData) => {
    if (isForgotPasswordMode) {
      handleForgotPassword(data);
    } else {
      handleSignIn(data);
    }
  };

  useEffect(() => {
    if (!isLogging && !isResettingPassword) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);
    return () => clearInterval(interval);
  }, [isLogging, isResettingPassword]);

  return (
    <div className="auth-page-hero"
      style={{
        background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${authBackground}) no-repeat center center`,
        backgroundSize: 'cover',
      }}>
      <div className="">
        <Container
          fluid
          className="d-flex align-items-center justify-content-center vh-100 auth-wrapper auth-cover"
        >
          <div
            className="p-4 card-signin shadow-lg text-center"
            style={{ maxWidth: "450px" }}
          >
            <div className="imageAreaBackgroundColor mb-1">
              <img
                src={logo}
                alt="loginimage"
                style={{ width: "200px", marginRight: "10px" }}
              />
            </div>
            <CardTitle tag="h2" className="fw-bold mb-1">
              {isForgotPasswordMode ? "Reset Your Password 🔐" : "Welcome to Curry King Super Admin 👋"}
            </CardTitle>
            <CardText className="mb-1">
              {isForgotPasswordMode
                ? "Enter your email address and we'll send you a link to reset your password."
                : "Please sign in with your authorized credentials"
              }
            </CardText>
            <Form
              className="auth-login-form mt-1"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="mb-3">
                <ControlledTextField
                  label=""
                  inputName="email"
                  inputType="email"
                  placeholder="Enter Your Email"
                  isRequired={true}
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  }}
                />
                {errors.email && (
                  <div className="text-danger mt-1 small">
                    {errors.email.message}
                  </div>
                )}
              </div>
              {!isForgotPasswordMode && (
                <div className="mb-3">
                  <ControlledTextField
                    label=""
                    inputName="password"
                    inputType="password"
                    placeholder="Enter Password"
                    isRequired={true}
                    control={control}
                    rules={{
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters"
                      }
                    }}
                  />
                  {errors.password && (
                    <div className="text-danger mt-1 small">
                      {errors.password.message}
                    </div>
                  )}
                </div>
              )}

              {isForgotPasswordMode ? (
                <Row className="mb-3">
                  <Col>
                    <Button
                      variant="outline-light"
                      onClick={handleCancelForgotPassword}
                      disabled={isResettingPassword}
                      className="me-2"
                      style={{ borderColor: '#590b21', color: '#590b21' }}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={!isValid || isResettingPassword}
                      className="btn my-2 py-2 btn-signin-color"
                    >
                      {isResettingPassword ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                          Sending{dots}
                        </>
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </Col>
                </Row>
              ) : (
                <>
                  {/* Sign In Button */}
                  <div className="d-grid">
                    {isLogging ? (
                      <button
                        type="submit"
                        className="btn my-2 py-2 btn-signin-color btn-block"
                        disabled={true}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                        Authenticating{dots}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn my-2 py-2 btn-signin-color btn-block"
                        disabled={!isValid}
                      >
                        Sign In
                      </button>
                    )}
                  </div>

                  {/* Forgot Password Link */}
                  <div className="d-flex justify-content-center mb-3">
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none p-0"
                      style={{ color: "#590b21", fontSize: "14px" }}
                      onClick={handleForgotPasswordMode}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  {/* Sign In with Google */}
                  <div className="d-grid mt-3">
                    <button
                      className="btn text-link-color d-flex align-items-center justify-content-center"
                      onClick={handleGoogleSignIn}
                      type="button"
                    >
                      <img
                        src={GoogleIcon}
                        alt="Google Icon"
                        style={{ width: "20px", marginRight: "10px" }}
                      />
                      Sign In with Google
                    </button>
                  </div>
                </>
              )}
            </Form>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default SignIn;